import { AxiosError } from "axios";
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from "@mui/material";
import { ArrowDownwardOutlined } from "@mui/icons-material";

interface Trace {
  id: number;
  trace: string;
}

interface RailsFrameworkError {
  status: number;
  error: string;
  exception: string;
  traces: {
    "application Trace": Trace[];
    "framework Trace": Trace[];
    "full Trace": Trace[];
  };
}

function isRailsFrameworkError(error: AxiosError<unknown>): error is AxiosError<RailsFrameworkError> {
  const data = error.response?.data;
  return (
    typeof data === "object" &&
    data !== null &&
    "status" in data &&
    "error" in data &&
    "exception" in data &&
    "traces" in data
  );
}

// Takes an exception like #<ActiveRecord::PendingMigrationError:"\n\nMigrations are pending..."> and
// parses it into an object

// Returns text transformed into HTML using simple formatting rules. Two or more consecutive newlines (\n\n or \r\n\r\n) are considered a paragraph and wrapped in <p> tags. One newline (\n or \r\n) is considered a linebreak and a <br /> tag is appended. This method does not remove the newlines from the text.

function simpleFormat(str: string) {
  const paragraphs = str.trim().split("\n\n");
  return paragraphs
    .map((paragraph) => {
      return "<p>" + paragraph.replace(/\n/, "<br />") + "</p>";
    })
    .join(" ");
}

function unescapeNewlines(str: string) {
  return str.replace(/\\n/g, "\n");
}

function parseRailsException(exception: string) {
  let matches = exception.match(/#<([^"]+):"(.*)"/);
  matches = matches || exception.match(/#<(.*):(.*)>/);
  if (matches) {
    const [, className, message] = matches;
    return { className, message: simpleFormat(unescapeNewlines(message)) };
  }
  return { className: "Unknown", message: exception };
}

export function LocalRailsError({ error }: { error: AxiosError<unknown> }) {
  if (isRailsFrameworkError(error) && error.response) {
    const applicationTrace = error.response.data.traces["application Trace"];
    const frameworkTrace = error.response.data.traces["framework Trace"];

    const closestTrace = applicationTrace.length > 0 ? applicationTrace : frameworkTrace;
    const { className, message } = parseRailsException(error.response.data.exception);
    return (
      <Stack flexDirection="column" alignItems="flex-start">
        <Typography variant="h5" color={"error"} sx={{ wordBreak: "break-word" }}>
          {error.response.data.status} {error.response.data.error} - {className}
        </Typography>
        <Typography
          sx={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
          fontWeight={700}
          dangerouslySetInnerHTML={{ __html: message }}
        ></Typography>
        <Typography variant={"h5"} color={"grey.600"} sx={{ my: 2 }}>
          Stack Trace
        </Typography>
        <Accordion variant={"outlined"} sx={{ flexGrow: 1, alignSelf: "stretch" }}>
          <AccordionSummary
            expandIcon={<ArrowDownwardOutlined />}
            sx={{ "& > .Mui-expanded": { marginTop: 0, marginBottom: 0 } }}
          >
            <Typography sx={{ wordBreak: "break-word" }}>{closestTrace[0]?.trace}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {closestTrace.slice(1).map((t) => (
              <Typography sx={{ wordBreak: "break-word" }}>{t.trace}</Typography>
            ))}
          </AccordionDetails>
        </Accordion>
        <Typography variant={"caption"} color={"grey.600"} mt={4}>
          This is a development only error page, shown to make troubleshooting faster and easier.
        </Typography>
      </Stack>
    );
  } else {
    return <>An unknown framework error occurred: {JSON.stringify(error.response?.data)}</>;
  }
}
